/**
 * Auth handlers for MSW
 * Handles password reset and MFA login endpoints for demo mode
 */

import { http, HttpResponse } from 'msw'
import { applyDelay, createErrorResponse } from '../utils'

const VALID_DEMO_CODE = '123456'
const DEMO_MFA_SESSION = 'demo-mfa-session-token'

// Helper to check if demo MFA is enabled
function getDemoMFAStatus(): boolean {
  if (typeof window !== 'undefined') {
    return localStorage.getItem('demo:mfaEnabled') === 'true'
  }
  return false
}

// Generate a mock JWT token
function generateMockToken(): string {
  const header = btoa(JSON.stringify({ alg: 'HS256', typ: 'JWT' }))
  const now = Math.floor(Date.now() / 1000)
  const payload = btoa(JSON.stringify({
    sub: 'demo-user-123',
    preferred_username: 'demo@a55.tech',
    email: 'demo@a55.tech',
    email_verified: true,
    given_name: 'Demo',
    family_name: 'User',
    name: 'Demo User',
    aud: 'demo-client-id',
    exp: now + 3600,
    iat: now,
    iss: 'https://keycloak.demo.example.com/realms/a55',
    token_use: 'id'
  }))
  const signature = btoa('demo-signature')
  return `${header}.${payload}.${signature}`
}

export const authHandlers = [
  /**
   * GET /api/auth/session
   * Get current session - returns demo user in demo mode
   */
  http.get('/api/auth/session', async ({ request }) => {
    const url = new URL(request.url)
    const searchParams = url.searchParams

    await applyDelay({ searchParams, preset: 'fast' })

    // Check for error simulation
    const errorParam = searchParams.get('_error')
    if (errorParam === '401') {
      return createErrorResponse(401, {
        message: 'Not authenticated'
      })
    }
    if (errorParam) {
      return createErrorResponse(500, {
        message: 'Unable to retrieve session'
      })
    }

    // Return demo user session
    const idToken = generateMockToken()
    const payloadPart = idToken.split('.')[1]

    return HttpResponse.json({
      isAuthenticated: true,
      user: {
        userId: 'demo-user-123',
        username: 'demo@a55.tech',
        email: 'demo@a55.tech',
        givenName: 'Demo',
        familyName: 'User',
        name: 'Demo User'
      },
      claims: payloadPart ? JSON.parse(atob(payloadPart)) : {},
      expiresAt: Date.now() + 3600000
    })
  }),

  /**
   * POST /api/auth/logout
   * Logout - always succeeds in demo mode
   */
  http.post('/api/auth/logout', async ({ request }) => {
    const url = new URL(request.url)
    const searchParams = url.searchParams

    await applyDelay({ searchParams, preset: 'fast' })

    return HttpResponse.json({
      success: true
    })
  }),

  /**
   * POST /api/auth/refresh
   * Refresh session - returns updated demo user session
   */
  http.post('/api/auth/refresh', async ({ request }) => {
    const url = new URL(request.url)
    const searchParams = url.searchParams

    await applyDelay({ searchParams, preset: 'fast' })

    // Check for error simulation
    const errorParam = searchParams.get('_error')
    if (errorParam === '401') {
      return createErrorResponse(401, {
        message: 'Session expired'
      })
    }

    // Return refreshed demo session
    const idToken = generateMockToken()
    const payloadPart = idToken.split('.')[1]

    return HttpResponse.json({
      isAuthenticated: true,
      user: {
        userId: 'demo-user-123',
        username: 'demo@a55.tech',
        email: 'demo@a55.tech',
        givenName: 'Demo',
        familyName: 'User',
        name: 'Demo User'
      },
      claims: payloadPart ? JSON.parse(atob(payloadPart)) : {},
      expiresAt: Date.now() + 3600000
    })
  }),

  /**
   * POST /api/auth/login
   * Demo login - simulates MFA challenge when MFA is enabled
   */
  http.post('/api/auth/login', async ({ request }) => {
    const url = new URL(request.url)
    const searchParams = url.searchParams

    await applyDelay({ searchParams, preset: 'normal' })

    // Check for error simulation
    const errorParam = searchParams.get('_error')
    if (errorParam === '401') {
      return createErrorResponse(401, {
        message: 'Invalid username or password'
      })
    }
    if (errorParam) {
      return createErrorResponse(500, {
        message: 'Unable to complete authentication'
      })
    }

    // Check if MFA is enabled for demo user
    if (getDemoMFAStatus()) {
      // Return MFA challenge
      return HttpResponse.json({
        challengeName: 'SOFTWARE_TOKEN_MFA',
        session: DEMO_MFA_SESSION,
        username: 'demo@a55.tech'
      })
    }

    // No MFA, return successful login
    const idToken = generateMockToken()
    const payloadPart = idToken.split('.')[1]

    return HttpResponse.json({
      isAuthenticated: true,
      user: {
        userId: 'demo-user-123',
        username: 'demo@a55.tech',
        email: 'demo@a55.tech',
        givenName: 'Demo',
        familyName: 'User',
        name: 'Demo User'
      },
      claims: payloadPart ? JSON.parse(atob(payloadPart)) : {},
      expiresAt: Date.now() + 3600000
    })
  }),

  /**
   * POST /api/auth/mfa/challenge
   * Complete MFA login challenge
   */
  http.post('/api/auth/mfa/challenge', async ({ request }) => {
    const url = new URL(request.url)
    const searchParams = url.searchParams

    await applyDelay({ searchParams, preset: 'normal' })

    // Check for error simulation
    const errorParam = searchParams.get('_error')
    if (errorParam === '400') {
      return createErrorResponse(400, {
        message: 'Invalid verification code'
      })
    }
    if (errorParam === '401') {
      return createErrorResponse(401, {
        message: 'Session expired. Please login again.'
      })
    }
    if (errorParam) {
      return createErrorResponse(500, {
        message: 'Unable to verify MFA code'
      })
    }

    const body = await request.json() as { username?: string, password?: string, code?: string }
    const { username, password, code } = body

    if (!username || !password || !code) {
      return createErrorResponse(400, {
        message: 'Username, password, and code are required'
      })
    }

    // Verify the code (accept 123456 in demo mode)
    if (code !== VALID_DEMO_CODE) {
      return createErrorResponse(400, {
        message: 'Invalid verification code'
      })
    }

    // Return successful login
    const idToken = generateMockToken()
    const payloadPart = idToken.split('.')[1]

    return HttpResponse.json({
      isAuthenticated: true,
      user: {
        userId: 'demo-user-123',
        username: 'demo@a55.tech',
        email: 'demo@a55.tech',
        givenName: 'Demo',
        familyName: 'User',
        name: 'Demo User'
      },
      claims: payloadPart ? JSON.parse(atob(payloadPart)) : {},
      expiresAt: Date.now() + 3600000
    })
  }),

  /**
   * POST /api/auth/forgot-password
   * Request password reset - always succeeds in demo mode
   */
  http.post('/api/auth/forgot-password', async ({ request }) => {
    const url = new URL(request.url)
    const searchParams = url.searchParams

    // Apply delay
    await applyDelay({ searchParams, preset: 'normal' })

    // Check for error simulation
    const errorParam = searchParams.get('_error')
    if (errorParam === '429') {
      return createErrorResponse(429, {
        message: 'Too many attempts. Please try again later.'
      })
    }
    if (errorParam) {
      return createErrorResponse(500, {
        message: 'Unable to process password reset request'
      })
    }

    // Always return success to prevent email enumeration
    return HttpResponse.json({
      success: true,
      message: 'If an account exists with this email, you will receive a password reset link.'
    })
  }),

  /**
   * POST /api/auth/reset-password
   * Confirm password reset with token from email link
   */
  http.post('/api/auth/reset-password', async ({ request }) => {
    const body = await request.json() as { token?: string, password?: string }

    if (!body.token || !body.password) {
      return createErrorResponse(400, {
        message: 'Token and password are required'
      })
    }

    if (body.token === 'invalid') {
      return createErrorResponse(400, {
        message: 'Invalid or expired reset link'
      })
    }

    return HttpResponse.json({
      success: true,
      message: 'Password has been reset successfully.'
    })
  })
]
